import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendAgentMessageStream } from '../../services/api';
import { hasAnyPermission } from '../../services/authService';
import { useTranslation } from '../../context/LanguageContext';
import './AiAssistant.css';

/* ===== AI 어시스턴트 — PNG 시안「AI 어시스턴트 FAB」「AI 어시스턴트 패널」=====
 * 화면 우하단 둥근 버튼(✦ AI 묻기) → 오른쪽 460px 패널. 패널은 헤더("AI 어시스턴트", "전체 분석 열기"→/ai-analysis),
 * 현재 맥락 칩(사이드바 메뉴명 + 사출기 이름), 대화(사용자 파랑 말풍선 / AI 답변), 추천 질문, 입력 + 전송.
 *
 * 답변은 AI 분석 화면과 같은 스트리밍 API(services/api.sendAgentMessageStream → /llm/agent/stream)로 받아 글자가 오는 대로 보여 준다.
 * (비스트리밍 /llm/agent 는 AI 서비스 쪽에서 'Orchestrator ... process_request' 오류가 나서 쓰지 않는다 — 2026-10-02)
 * 시안의 "근거 트렌드 미니 차트"와 "이 화면 관련 AI 제안 n건" 말풍선은 그 데이터를 내려주는 API 가 없어 넣지 않았다(가짜 데이터 금지).
 * 알람 상세 드로어의 "AI에게 원인 묻기"는 window 이벤트 'smi:ask-ai' 로 질문을 넘겨 이 패널이 열리며 바로 전송한다.
 * 대화는 페이지 이동(MainLayout 재마운트) 후에도 이어지도록 sessionStorage 에 둔다. */

const CHAT_STORAGE_KEY = 'smiAiChat';
const MAX_HISTORY = 20;

/* 경로 → 사이드바 메뉴 번역 키 (현재 맥락 칩) */
const PATH_LABEL_KEYS = {
  '/enterprise': 'sidebar.enterprise.dashboard',
  '/enterprise-factory-overview': 'sidebar.enterprise.factoryOverview',
  '/enterprise-dataflow': 'sidebar.enterprise.dataflow',
  '/factory': 'sidebar.factory.overview',
  '/factory-machine-overview': 'sidebar.factory.machineOverview',
  '/factory-defect-history': 'sidebar.factory.defectHistory',
  '/realtime-shot-status': 'sidebar.factory.realtimeShot',
  '/machine': 'sidebar.machine.dashboard',
  '/equipment-board': 'sidebar.machine.equipmentBoard',
  '/ai-analysis': 'sidebar.ai.analysis',
  '/home': 'common.home'
};

const readChat = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(CHAT_STORAGE_KEY));
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};

function AiAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const tr = useCallback((k, p) => t(`header.ai.${k}`, p), [t]);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(readChat);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try { sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY * 2))); } catch (e) { /* 저장 실패는 무시 */ }
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  /* 현재 맥락: 메뉴명 (+ 사출기 이름) */
  const contextLabel = useMemo(() => {
    const key = PATH_LABEL_KEYS[location.pathname];
    const parts = [];
    if (key) parts.push(t(key));
    const m = location.state?.machineData;
    if (m?.name) parts.push(m.name);
    return parts.join(' · ') || location.pathname;
  }, [location.pathname, location.state, t]);

  const send = useCallback(async (text) => {
    const question = (text || '').trim();
    if (!question || busy) return;
    const history = messages.filter((m) => m.role === 'user' || m.role === 'assistant').slice(-MAX_HISTORY);
    setMessages((prev) => [...prev, { role: 'user', content: question, at: Date.now() }]);
    setInput('');
    setBusy(true);
    /* 답변 자리를 먼저 만들고, 토큰이 올 때마다 그 자리를 채운다 */
    const at = Date.now();
    const setAnswer = (content, extra = {}) => setMessages((prev) => {
      const next = [...prev];
      const i = next.findIndex((m) => m.role === 'assistant' && m.at === at);
      const msg = { role: 'assistant', content, at, ...extra };
      if (i >= 0) next[i] = msg; else next.push(msg);
      return next;
    });
    try {
      const res = await sendAgentMessageStream(question, history, (token, fullText) => setAnswer(fullText, { streaming: true }));
      const answer = res?.natural_language || res?.error;
      if (answer) setAnswer(String(answer), { failed: !!(res.success === false && !res.natural_language) });
      else setAnswer(tr('noAnswer'), { failed: true });
    } catch (e) {
      console.error('AI assistant request failed:', e);
      setAnswer(tr('failed'), { failed: true });
    } finally {
      setBusy(false);
    }
  }, [busy, messages, tr]);

  /* 알람 상세 → "AI에게 원인 묻기" */
  useEffect(() => {
    const onAsk = (e) => {
      const q = e?.detail?.question;
      setOpen(true);
      if (q) send(q);
    };
    window.addEventListener('smi:ask-ai', onAsk);
    return () => window.removeEventListener('smi:ask-ai', onAsk);
  }, [send]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 50);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!hasAnyPermission(['ai.view'])) return null;   /* 사이드바 AI 메뉴와 같은 권한 */
  if (location.pathname === '/ai-analysis') return null;   /* 전체 AI 분석 화면에서는 같은 기능이 이미 화면 전체에 있다 */

  const suggestions = [tr('q1'), tr('q2'), tr('q3')];

  return ReactDOM.createPortal(
    <>
      {!open && (
        <button type="button" className="ai-fab" onClick={() => setOpen(true)} title={tr('title')} aria-label={tr('title')}>
          <span className="ai-fab-star">✦</span>
          <span className="ai-fab-label">AI</span>
          <span className="ai-fab-sub">{tr('fab')}</span>
        </button>
      )}

      {open && (
        <aside className="ai-panel" role="dialog" aria-label={tr('title')}>
          <div className="ai-head">
            <span className="ai-logo">AI</span>
            <h2 className="ai-title">{tr('title')}</h2>
            <button type="button" className="ai-link" onClick={() => { setOpen(false); navigate('/ai-analysis'); }}>{tr('openFull')}</button>
            <button type="button" className="ai-icon-btn" onClick={() => setOpen(false)} title={t('common.close')} aria-label={t('common.close')}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* 새 대화 — 헤더 바로 아래에 잘 보이는 버튼으로 둔다 (예전엔 입력창 아래 작은 글자 "대화 지우기") */}
          <div className="ai-toolbar">
            <div className="ai-context">
              <span className="ai-context-dot" aria-hidden="true" />
              <span className="ai-context-label">{tr('context')}</span>
              <span className="ai-context-value">{contextLabel}</span>
            </div>
            <button type="button" className="ai-new-chat" onClick={() => { setMessages([]); setInput(''); }} disabled={busy || messages.length === 0} title={tr('newChatHint')}>
              <span className="material-symbols-outlined">add_comment</span>{tr('newChat')}
            </button>
          </div>

          <div className="ai-messages" ref={listRef}>
            {messages.length === 0 && <div className="ai-empty">{tr('empty')}</div>}
            {messages.map((m, i) => (
              <div key={`${m.at}-${i}`} className={`ai-msg ai-msg-${m.role} ${m.failed ? 'ai-msg-failed' : ''}`}>
                {m.content}
              </div>
            ))}
            {busy && !messages.some((m) => m.streaming) && <div className="ai-msg ai-msg-assistant ai-msg-busy">{tr('thinking')}</div>}

            {messages.length === 0 && (
              <div className="ai-suggest">
                <div className="ai-suggest-title">{tr('suggestionsTitle')}</div>
                {suggestions.map((q) => (
                  <button key={q} type="button" className="ai-suggest-item" onClick={() => send(q)} disabled={busy}>
                    <span>{q}</span>
                    <span className="material-symbols-outlined">north_east</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <form className="ai-input-row" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <input
              ref={inputRef}
              className="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={tr('placeholder')}
              disabled={busy}
            />
            <button type="submit" className="ai-send" disabled={busy || !input.trim()}>{tr('send')}</button>
          </form>
        </aside>
      )}
    </>,
    document.body
  );
}

export default AiAssistant;
